/**
 * Panel that slides in from the side to create a new queue.
 */
import Drawer from '@material-ui/core/Drawer';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Checkbox} from '@wandb/weave/components/Checkbox';
import {TextField} from '@wandb/weave/components/Form/TextField';
import {ApolloCurrentQueryResult} from 'apollo-client';
import produce from 'immer';
import React, {useCallback, useEffect, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {toast} from 'react-toastify';

import YAML from '../../../../services/yaml';
import {
  AllQueuesQuery,
  RunQueueAccessType,
  RunQueuePrioritizationMode,
  useCreateDefaultResourceConfigMutation,
  useCreateRunQueueMutation,
  useUpsertModelMutation,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import DocUrls from '../../../util/doc_urls';
import {
  extractErrorMessageFromApolloError,
  propagateErrorsContext,
} from '../../../util/errors';
import {queue} from '../../../util/urls';
import {ResizableDrawer} from '../../common/ResizableDrawer/ResizableDrawer';
import {CodeEditor} from '../CodeEditor';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../constants';
import {
  JsonFromFormState,
  ParseYamlWithHandlebarsToJson,
  VariableFormState,
} from '../DefaultResourceConfig/defaultResourceConfigUtil';
import EmptyTemplateVariables from '../DefaultResourceConfig/EmptyTemplateVariables';
import {DefineResourceTemplateVariablesForm} from '../DefaultResourceConfig/TemplateVariableForm';
import {RESOURCE_CONFIGS} from '../LaunchConfigEditor';
import {ResourceType} from '../utilTypes';
import * as S from './CreateQueueDrawer.styles';
import {SelectEntity} from './SelectEntity';
import {SelectResourceType} from './SelectResourceType';

type CreateQueueDrawerProps = {
  open: boolean;
  toggleDrawer: () => void;
  entityName: string;
  refetchQueues?: () => Promise<ApolloCurrentQueryResult<AllQueuesQuery>>;
};

export const CreateQueueDrawer = ({
  open,
  toggleDrawer,
  entityName,
  refetchQueues,
}: CreateQueueDrawerProps) => {
  const [prioritizationMode, setPrioritizationMode] = useState(true);
  const [width, setWidth] = useState(500);
  const onSetWidth = (newWidth: number) => {
    setWidth(newWidth);
  };

  const [variables, setVariables] = useState<{
    [key: string]: VariableFormState;
  }>({});

  const [displayMode, setDisplayMode] = useState('json');
  const buttonColorYaml = displayMode === 'yaml' ? 'secondary' : 'ghost';
  const buttonColorJson = displayMode === 'json' ? 'secondary' : 'ghost';
  const history = useHistory();
  const [selectedEntity, setSelectedEntity] = useState(entityName);
  const [isCreating, setIsCreating] = useState(false);
  const [queueName, setQueueName] = useState('');
  const [resourceType, setResourceType] =
    useState<ResourceType>('local-container');
  const [config, setConfig] = useState('');
  useEffect(() => {
    const resConfig = RESOURCE_CONFIGS[resourceType];
    if (displayMode === 'yaml') {
      setConfig(YAML.stringify(resConfig, {indent: 2}));
    } else {
      setConfig(JSON.stringify(resConfig, null, 2));
    }
    /* eslint-disable-next-line  react-hooks/exhaustive-deps --
     * Eslint wants displayMode as a dependency but this will
     * cause default resource args being set when the user
     * switches between json and yaml.
     **/
  }, [resourceType]);

  // This is a callback used by the child components TemplateVariableForm
  // to update the variables state in this component.
  const updateVariables = useCallback(
    (name: string, state: VariableFormState) => {
      setVariables(prevVariables =>
        produce(prevVariables, draftVariables => {
          draftVariables[name] = state;
        })
      );
    },
    [setVariables]
  );

  const onNameChange = (value: string) => {
    setQueueName(value);
  };
  const onClose = (e: React.MouseEvent) => {
    toggleDrawer();
  };

  const onConfigChange = (newValue: string) => {
    setConfig(newValue);
  };

  const [upsertProject] = useUpsertModelMutation();
  const [createDefaultResourceConfig] =
    useCreateDefaultResourceConfigMutation();
  const [createRunQueue] = useCreateRunQueueMutation();

  const setEditorType = (type: string) => {
    if (displayMode === type) {
      return;
    }
    // Convert the blob from previous format to current format
    if (type === 'json') {
      try {
        const parsed = ParseYamlWithHandlebarsToJson(config);
        setConfig(JSON.stringify(parsed, null, 2));
        setDisplayMode('json');
      } catch (e) {
        toast.error('Invalid YAML');
        return;
      }
    } else if (type === 'yaml') {
      try {
        const parsed = JSON.parse(config);
        setConfig(YAML.stringify(parsed, {indent: 2}));
        setDisplayMode('yaml');
      } catch (e) {
        toast.error('Invalid JSON');
        return;
      }
    }
  };

  // TODO: Also check for remaining <REQUIRED>
  const validateConfig = (contents: string) => {
    try {
      if (displayMode === 'yaml') {
        ParseYamlWithHandlebarsToJson(config);
        return true;
      } else {
        JSON.parse(contents);
        return true;
      }
    } catch (e) {
      toast.error('Config must be valid ' + displayMode.toUpperCase());
      return false;
    }
  };

  const handleConfigVariables = () => {
    const regex = /(?<=\{\{)(.*?)(?=\}\})/g;
    const matches = config.match(regex);

    const newVariables = matches
      ? matches.reduce((memo: {[key: string]: VariableFormState}, match) => {
          const name = match.trim();
          // Keeping or initializing the variables
          memo[name] = variables[name] ?? {name};
          return memo;
        }, {})
      : {};

    setVariables(newVariables);
  };

  // TODO: We previously had support for private queues in teams.
  //       Is it worth reviving?
  const onCreateQueue = async () => {
    Analytics.track('clicked create queue', {
      location: 'create queue drawer',
    });
    if (!validateConfig(config)) {
      return;
    }
    setIsCreating(true);

    // First, ensure we have the necessary project for an entity-scoped queue.
    const createProjectResponse = await upsertProject({
      variables: {
        entityName: selectedEntity,
        name: DEFAULT_LAUNCH_PROJECT_NAME,
      },
    });
    if (createProjectResponse.errors != null) {
      toast('Error creating entity queue project.');
      setIsCreating(false);
      return;
    }

    const completeConfig = {
      resource_args: {
        [resourceType]:
          displayMode === 'yaml'
            ? ParseYamlWithHandlebarsToJson(config)
            : JSON.parse(config),
      },
    };
    const variablesJSON: {[key: string]: any} = {};
    try {
      for (const [key, value] of Object.entries(variables)) {
        const json = JsonFromFormState(key, value);
        if (json == null) {
          setIsCreating(false);
          return;
        }
        variablesJSON[key] = json;
      }
    } catch (e) {
      toast.error('Error parsing variables: ' + e);
      setIsCreating(false);
      return;
    }

    // We create one default resource config per-queue.
    // This is different than previous iterations where default resource configs
    // were defined in a separate step and then user could select one during queue creation.
    let defaultResourceConfigID;
    let validationErrors = [];
    try {
      const {data: dataConfig} = await createDefaultResourceConfig({
        variables: {
          entityName: selectedEntity,
          resource: resourceType,
          config: JSON.stringify(completeConfig, null, 2),
          templateVariables: JSON.stringify(variablesJSON),
        },
        context: propagateErrorsContext(),
      });
      defaultResourceConfigID =
        dataConfig?.createDefaultResourceConfig?.defaultResourceConfigID;
      validationErrors =
        dataConfig?.createDefaultResourceConfig?.schemaValidationErrors ?? [];
    } catch (e) {
      const err = extractErrorMessageFromApolloError(e) ?? `${e}`;
      toast.error(`Error in queue configuration: ${err}`);
      setIsCreating(false);
      return;
    }

    const {data} = await createRunQueue({
      variables: {
        entityName: selectedEntity,
        projectName: DEFAULT_LAUNCH_PROJECT_NAME,
        queueName,
        access: RunQueueAccessType.Project,
        defaultResourceConfigID,
        prioritizationMode: prioritizationMode
          ? RunQueuePrioritizationMode.V0
          : RunQueuePrioritizationMode.Disabled,
      },
    });
    setIsCreating(false);
    toggleDrawer();
    if (refetchQueues) {
      await refetchQueues();
    }

    Analytics.track('create queue success', {
      location: 'create queue drawer',
    });

    const queueID = data?.createRunQueue?.queueID;
    if (queueID) {
      if (validationErrors.length > 0) {
        const queueURL = queue(selectedEntity, queueID, 'config');
        history.push(queueURL, {validationErrors});
      } else {
        const queueURL = queue(selectedEntity, queueID, 'agents');
        history.push(queueURL);
      }
    }
  };
  const onSelectResourceType = (selectedResourceType: ResourceType) => {
    setResourceType(selectedResourceType);
  };
  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <ResizableDrawer
        startWidth={width}
        minWidth={400}
        onSetWidth={onSetWidth}>
        <S.DrawerContents>
          <S.DrawerLayout>
            <S.TopPanel>
              <S.HeaderRow>
                <S.Header>Create a queue</S.Header>
                <S.Close>
                  <Button
                    onClick={onClose}
                    icon="close"
                    size="large"
                    variant="ghost"
                  />
                </S.Close>
              </S.HeaderRow>
              <S.Description>
                Queues provide your team the compute resources for launching
                jobs at scale. After queue creation, you'll next add an agent.
              </S.Description>
              <S.FormLabelRow>
                <S.FormLabel>Entity</S.FormLabel>
                <S.FormLabelHelp>
                  You must be an admin of your entity to create a queue.
                </S.FormLabelHelp>
              </S.FormLabelRow>
              <SelectEntity
                entityAdminOnly={true}
                showRole={true}
                value={selectedEntity}
                onSelectEntity={setSelectedEntity}
              />
              <S.FormLabel>Queue name</S.FormLabel>
              <TextField
                placeholder="Name your queue"
                value={queueName}
                onChange={onNameChange}
                autoFocus={true}
                dataTest="name-queue"
              />
              <S.FormLabel>Resource</S.FormLabel>
              <SelectResourceType
                value={resourceType}
                onSelectResourceType={onSelectResourceType}
              />
              <S.FormCheckboxRow>
                <S.InlineFormLabel>Enable Prioritization</S.InlineFormLabel>
                <Checkbox
                  data-test="prioritization-checkbox"
                  checked={prioritizationMode}
                  onCheckedChange={checked => {
                    setPrioritizationMode(checked === true);
                  }}
                />
              </S.FormCheckboxRow>
              <S.HintText>Priority can be requested at launch.</S.HintText>
              <S.FormLabelRow>
                <S.FormLabel>Configuration</S.FormLabel>
                <S.FormLabelButtons>
                  <Button
                    size="small"
                    variant={buttonColorJson}
                    onClick={() => setEditorType('json')}>
                    JSON
                  </Button>
                  <Button
                    size="small"
                    variant={buttonColorYaml}
                    onClick={() => setEditorType('yaml')}>
                    YAML
                  </Button>
                </S.FormLabelButtons>
              </S.FormLabelRow>
              <CodeEditor
                value={config}
                language={displayMode}
                onChange={onConfigChange}
                dataTest="queue-config-editor"
              />
              <div>
                <S.FormLabelRow>
                  <S.FormLabel>Launch-time overrides</S.FormLabel>
                  <S.FormLabelButtons>
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={handleConfigVariables}>
                      Parse configuration
                    </Button>
                  </S.FormLabelButtons>
                </S.FormLabelRow>
                {Object.keys(variables).length === 0 ? (
                  <EmptyTemplateVariables />
                ) : (
                  <DefineResourceTemplateVariablesForm
                    initialState={variables}
                    rawConfigStr={config}
                    onUpdateVariable={updateVariables}
                  />
                )}
              </div>

              <S.HelpRow>
                <S.HelpMessage>
                  Need help? Check out our documentation:
                </S.HelpMessage>
                <TargetBlank href={DocUrls.createQueue}>
                  <Button icon="documentation" size="large" variant="secondary">
                    Documentation
                  </Button>
                </TargetBlank>
              </S.HelpRow>
            </S.TopPanel>

            <Button
              data-test="create-queue-finish"
              className="w-full"
              variant="primary"
              size="large"
              onClick={onCreateQueue}
              disabled={!selectedEntity || !queueName || isCreating}>
              Create queue
            </Button>
          </S.DrawerLayout>
        </S.DrawerContents>
      </ResizableDrawer>
    </Drawer>
  );
};
